/* eslint-disable @typescript-eslint/no-explicit-any, no-console */
/**
 * Google Cast Web Sender SDK Manager.
 *
 * Dual-mode:
 *   - Browser: uses the Google Cast Web Sender SDK (chrome.cast / cast.framework)
 *   - Electron: uses window.electronAPI.cast (IPC → castv2-client in main process)
 */

import { CAST_NAMESPACE } from '@proclaim/shared';

export type CastManagerEvent =
  | { type: 'sdk_ready' }
  | { type: 'cast_state_changed'; state: string }
  | { type: 'devices_changed'; devices: ElectronChromecastDevice[] }
  | { type: 'session_started'; deviceName: string }
  | { type: 'session_ended'; deviceName?: string } // deviceName present in Electron (per-device)
  | { type: 'receiver_log'; log: ReceiverLogEntry }
  | { type: 'error'; message: string };

type Listener = (event: CastManagerEvent) => void;

const IS_ELECTRON = typeof window !== 'undefined' && !!window.electronAPI;

class CastManager {
  private listeners: Set<Listener> = new Set();
  private initialized = false;
  private appId = '';

  // Electron-mode state
  private _devices: ElectronChromecastDevice[] = [];
  private _cleanups: Array<() => void> = [];

  /** Discovered Chromecast devices (Electron mode only). */
  get devices(): ElectronChromecastDevice[] {
    return [...this._devices];
  }

  init(appId: string): void {
    if (this.initialized) return;
    this.initialized = true;
    this.appId = appId;

    if (IS_ELECTRON) {
      this.setupElectron();
      return;
    }

    console.log('[Cast] init() — App ID:', appId);
    console.log('[Cast] __castApiIsAvailable:', (window as any).__castApiIsAvailable);

    if ((window as any).__castApiIsAvailable === true) {
      console.log('[Cast] SDK ya disponible, llamando setup()');
      this.setup();
      return;
    }
    if ((window as any).__castApiIsAvailable === false) {
      console.warn('[Cast] SDK no disponible (solo funciona en Chrome)');
      this.emit({ type: 'error', message: 'Cast SDK no disponible en este navegador' });
      return;
    }

    console.log('[Cast] Esperando evento cast-api-available...');
    window.addEventListener(
      'cast-api-available',
      (e: Event) => {
        const available = (e as CustomEvent<{ isAvailable: boolean }>).detail.isAvailable;
        if (available) {
          this.setup();
        } else {
          this.emit({ type: 'error', message: 'Cast SDK no disponible en este navegador' });
        }
      },
      { once: true },
    );
  }

  private setup(): void {
    console.log('[Cast] setup() — App ID:', this.appId);
    const ctx = cast.framework.CastContext.getInstance();

    ctx.setOptions({
      receiverApplicationId: this.appId,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
    });

    ctx.addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      (event: any) => {
        console.log('[Cast] cast_state_changed:', event.castState);
        this.emit({ type: 'cast_state_changed', state: event.castState });
      },
    );

    ctx.addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      (event: cast.framework.SessionStateEventData) => {
        const { SessionState } = cast.framework;
        if (
          event.sessionState === SessionState.SESSION_STARTED ||
          event.sessionState === SessionState.SESSION_RESUMED
        ) {
          const device = event.session?.getCastDevice();
          this.emit({
            type: 'session_started',
            deviceName: device?.friendlyName ?? 'Chromecast',
          });
        } else if (
          event.sessionState === SessionState.SESSION_ENDED ||
          event.sessionState === SessionState.SESSION_START_FAILED
        ) {
          this.emit({ type: 'session_ended' });
        }
      },
    );

    const initialState = (ctx as any).getCastState?.() as string | undefined;
    console.log('[Cast] estado inicial:', initialState);
    if (initialState) {
      this.emit({ type: 'cast_state_changed', state: initialState });
    }

    // Exponer diagnóstico en window para llamar desde consola
    this.exposeDiagnostics(ctx);

    console.log('[Cast] SDK listo');
    this.emit({ type: 'sdk_ready' });
  }

  private setupElectron(): void {
    const eApi = window.electronAPI!.cast;
    eApi.startDiscovery();

    this._cleanups.push(
      eApi.onDeviceFound((device) => {
        if (!this._devices.find((d) => d.name === device.name)) {
          this._devices.push(device);
        }
        this.emit({ type: 'cast_state_changed', state: 'NOT_CONNECTED' });
        this.emit({ type: 'devices_changed', devices: [...this._devices] });
      }),
      eApi.onDeviceLost((name) => {
        this._devices = this._devices.filter((d) => d.name !== name);
        if (this._devices.length === 0) {
          this.emit({ type: 'cast_state_changed', state: 'NO_DEVICES_AVAILABLE' });
        }
        this.emit({ type: 'devices_changed', devices: [...this._devices] });
      }),
      eApi.onSessionEnded((deviceName: string) => {
        this.emit({ type: 'session_ended', deviceName });
      }),
      eApi.onReceiverLog((log) => {
        this.emit({ type: 'receiver_log', log });
      }),
    );

    // Defer so the caller can register its listener via castManager.on() before these fire
    setTimeout(() => {
      this.emit({ type: 'sdk_ready' });
      this.emit({ type: 'cast_state_changed', state: 'NO_DEVICES_AVAILABLE' });
    }, 0);
  }

  /**
   * Expone window.__castDiag() para diagnóstico desde la consola del navegador.
   *
   * Pruebas disponibles:
   *   __castDiag()           → estado actual
   *   __castDiag('default')  → reinicializa con DEFAULT_MEDIA_RECEIVER_APP_ID
   *                            para ver si hay Chromecasts en la red
   *   __castDiag('custom')   → vuelve al App ID original
   */
  private exposeDiagnostics(ctx: cast.framework.CastContext): void {
    const originalAppId = this.appId;

    (window as any).__castDiag = (mode?: string) => {
      const state = (ctx as any).getCastState?.();
      const session = ctx.getCurrentSession();

      console.group('[Cast Diagnostics]');
      console.log('App ID actual:', (ctx as any).options_?.receiverApplicationId ?? originalAppId);
      console.log('Cast state:', state);
      console.log('Sesión activa:', !!session);
      console.log('cast.framework disponible:', typeof cast !== 'undefined');

      if (mode === 'default') {
        console.log('--- Probando con DEFAULT_MEDIA_RECEIVER_APP_ID (CC1AD845)...');
        console.log('    Si aparece NOT_CONNECTED → la red funciona, problema es el registro del dispositivo');
        console.log('    Si sigue NO_DEVICES_AVAILABLE → problema de red (mismo WiFi? mDNS bloqueado?)');
        ctx.setOptions({
          receiverApplicationId: 'CC1AD845',
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        setTimeout(() => {
          console.log('Estado después de cambiar a DEFAULT:', (ctx as any).getCastState?.());
          console.groupEnd();
        }, 3000);
        return 'Esperando 3 segundos... revisa el log';
      }

      if (mode === 'custom') {
        console.log('--- Volviendo a App ID:', originalAppId);
        ctx.setOptions({
          receiverApplicationId: originalAppId,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        setTimeout(() => {
          console.log('Estado después de restaurar App ID:', (ctx as any).getCastState?.());
          console.groupEnd();
        }, 3000);
        return 'Esperando 3 segundos... revisa el log';
      }

      console.groupEnd();
      return { state, session: !!session };
    };

    console.log('[Cast] Diagnóstico disponible en consola:');
    console.log('  __castDiag()           → estado actual');
    console.log('  __castDiag("default")  → prueba con receiver por defecto');
    console.log('  __castDiag("custom")   → restaura tu App ID');
  }

  async requestSession(deviceName?: string): Promise<void> {
    if (IS_ELECTRON) {
      const device = deviceName
        ? this._devices.find((d) => d.name === deviceName)
        : this._devices[0];
      if (!device) throw new Error('No hay dispositivos Chromecast disponibles');
      const result = await window.electronAPI!.cast.launch(device, this.appId ?? '');
      if (!result.ok) throw new Error(result.error ?? 'Error al conectar con Chromecast');
      // Always use device.name (mDNS discovery key) — result.deviceName is the Cast
      // session displayName which can differ, causing castConnectingDevices to never clear.
      this.emit({ type: 'session_started', deviceName: device.name });
      return;
    }
    const ctx = cast.framework.CastContext.getInstance();
    await ctx.requestSession();
  }

  async stopDevice(deviceName: string): Promise<void> {
    if (!IS_ELECTRON) return;
    await window.electronAPI!.cast.stopDevice(deviceName);
  }

  /**
   * Re-invoca setOptions() para que el SDK compruebe si existe una sesión activa
   * con el mismo appId (ORIGIN_SCOPED) y reconecte automáticamente sin gesto del
   * usuario. Llamar esto cuando la sesión cae y el tab vuelve a ser visible.
   */
  rejoin(): void {
    if (IS_ELECTRON) return; // castv2 auto-reconnects via onSessionEnded → store restarts
    if (!this.appId || !this.initialized) return;
    try {
      const ctx = cast.framework.CastContext.getInstance();
      ctx.setOptions({
        receiverApplicationId: this.appId,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
    } catch (e) {
      console.warn('[Cast] rejoin() falló:', e);
    }
  }

  async sendMessage(message: object): Promise<void> {
    if (IS_ELECTRON) {
      const result = await window.electronAPI!.cast.send(message);
      if (!result.ok) throw new Error(result.error ?? 'Sin sesión Cast activa');
      return;
    }
    const ctx = cast.framework.CastContext.getInstance();
    const session = ctx.getCurrentSession();
    if (!session) throw new Error('No hay sesión Cast activa');
    await session.sendMessage(CAST_NAMESPACE, message);
  }

  endSession(): void {
    if (IS_ELECTRON) {
      window.electronAPI!.cast.stop().catch(() => {});
      return;
    }
    const ctx = cast.framework.CastContext.getInstance();
    const session = ctx.getCurrentSession();
    session?.endSession(true);
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: CastManagerEvent): void {
    for (const l of this.listeners) l(event);
  }
}

export const castManager = new CastManager();
